/* eslint-disable unused-imports/no-unused-vars */
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";
import {
  dispatchError,
  dispatchTransactionStatus,
  handlerPaymentMethodAction,
  statusHandler,
} from "configs/paymentApiConfig";
import { API_TAGS } from "constants/apiTags";
import {
  AnindaHavaleDepositRequest,
  AnindaHavaleDepositResponse,
  AnindaHavaleWithdrawRequest,
  AnindaHavaleWithdrawResponse,
  ApayDepositRequest,
  ApayDepositResponse,
  ApayWithdrawRequest,
  ApayWithdrawResponse,
  AzarpayDepositRequest,
  AzarpayDepositResponse,
  AzarpayWithdrawRequest,
  AzarpayWithdrawResponse,
  BankCardsDepositRequest,
  BankCardsDepositResponse,
  BanksResponse,
  BitoloDepositRequest,
  BitoloDepositResponse,
  BitoloWithdrawRequest,
  BitoloWithdrawResponse,
  BovaWithdrawRequest,
  BovaWithdrawResponse,
  CashixDepositRequest,
  CashixDepositResponse,
  CashixWithdrawRequest,
  CashixWithdrawResponse,
  ConstantposDepositRequest,
  ConstantposDepositResponse,
  ConstantposWithdrawRequest,
  ConstantposWithdrawResponse,
  CorefyDepositRequest,
  CorefyDepositResponse,
  CorefyWithdrawRequest,
  CorefyWithdrawResponse,
  EpulDepositRequest,
  EpulDepositResponse,
  EpulWithdrawRequest,
  EpulWithdrawResponse,
  ExPayWithdrawRequest,
  ExPayWithdrawResponse,
  ExpressHavaleDepositRequest,
  ExpressHavaleDepositResponse,
  ExpressHavaleWithdrawRequest,
  ExpressHavaleWithdrawResponse,
  HavaleHeraDepositRequest,
  HavaleHeraDepositResponse,
  HavaleHeraWithdrawRequest,
  HavaleHeraWithdrawResponse,
  HavaleVipDepositRequest,
  HavaleVipDepositResponse,
  HavaleVipWithdrawRequest,
  HavaleVipWithdrawResponse,
  MaksiparaDepositRequest,
  MaksiparaDepositResponse,
  MaksiparaWithdrawRequest,
  MaksiparaWithdrawResponse,
  MefeteDepositRequest,
  MefeteDepositResponse,
  MefeteWithdrawRequest,
  MefeteWithdrawResponse,
  MethodsResponseType,
  MpayDepositRequest,
  MpayDepositResponse,
  OctopaysDepositRequest,
  OctopaysDepositResponse,
  OctopaysWithdrawRequest,
  OctopaysWithdrawResponse,
  PaparaDepositRequest,
  PaparaDepositResponse,
  PaparaWithdrawRequest,
  PaparaWithdrawResponse,
  PaycosDepositRequest,
  PaycosDepositResponse,
  PaycosWithdrawRequest,
  PaycosWithdrawResponse,
  PayfixDepositRequest,
  PayfixDepositResponse,
  PayfixWithdrawRequest,
  PayfixWithdrawResponse,
  PaykassmaDepositActivateRequest,
  PaykassmaDepositActivateResponse,
  PaykassmaDepositCreateRequest,
  PaykassmaDepositCreateResponse,
  WangupayDepositRequest,
  WangupayDepositResponse,
  PaykassmaWithdrawRequest,
  PaykassmaWithdrawResponse,
  PaymentMethodsQueryArgs,
  SimplepayDepositRequest,
  SimplepayDepositResponse,
  SimplepayWithdrawRequest,
  SimplepayWithdrawResponse,
  SmartProPayDepositRequest,
  SmartProPayDepositResponse,
  SmartProPayWithdrawRequest,
  SmartProPayWithdrawResponse,
  TroyDepositRequest,
  TroyDepositResponse,
  UpiVFastDepositRequest,
  UpiVFastDepositResponse,
  ValerpayDepositRequest,
  ValerpayDepositResponse,
  ValerpayWithdrawRequest,
  ValerpayWithdrawResponse,
  WangupayWithdrawRequest,
  WangupayWithdrawResponse,
  WellxpayDepositRequest,
  WellxpayDepositResponse,
  WellxpayWithdrawRequest,
  WellxpayWithdrawResponse,
  ZenpayDepositRequest,
  ZenpayDepositResponse,
  ZenpayWithdrawRequest,
  ZenpayWithdrawResponse,
  VevopayDepositRequest,
  VevopayDepositResponse,
  SpayzDepositResponse,
  SpayzDepositRequest,
  SpayzWithdrawResponse,
  SpayzWithdrawRequest,
  EasypaygameDepositResponse,
  EasypaygameDepositRequest,
  EasypaygameWithdrawResponse,
  EasypaygameWithdrawRequest,
  UmmoneyDepositResponse,
  UmmoneyDepositRequest,
  UmmoneyWithdrawResponse,
  UmmoneyWithdrawRequest,
  PayokDepositResponse,
  PayokDepositRequest,
  PayokWithdrawRequest,
  PayokWithdrawResponse,
  InwizoDepositResponse,
  InwizoDepositRequest,
  InwizoWithdrawResponse,
  InwizoWithdrawRequest,
  BovaDepositResponse,
  BovaDepositRequest,
  BanksMethodsQueryArgs,
  BanksApiResponse,
  CryptoDepositRequest,
  CryptoDepositResponse,
} from "types/PaymentMethodsTypes";
import url from "url/index";
import { currentCurrency } from "redux/user/selectors";
import { RootState } from "redux/store";
import { setHostname } from "utils/hostname";
import { getAuthToken } from "helpers/authToken";
import { transformPaymentMethods } from "utils/transformPaymentMethods";
import { APP_VERSION } from "constants/variables";
import {
  setBanks,
  setCryptoMethods,
  setDepositMethods,
  setLoading,
  setServiceError,
  setWithdrawMethods,
} from "./slice";

export const paymentApi = createApi({
  reducerPath: "paymentApi",
  baseQuery: fetchBaseQuery({
    baseUrl: setHostname(),
    prepareHeaders: (headers) => {
      const authHeaders = getAuthToken();
      if (authHeaders) {
        Object.entries(authHeaders).forEach(([key, value]) => {
          headers.set(key, value as string);
        });
      }

      headers.set("f-v", APP_VERSION);

      return headers;
    },
  }),
  tagTypes: [API_TAGS.PAYMENTS],
  endpoints: (build) => ({
    methods: build.query<MethodsResponseType, PaymentMethodsQueryArgs>({
      query: (args) => ({
        url: url.config.api.payments.methods,
        params: {
          type: args.type,
        },
      }),
      providesTags: [API_TAGS.PAYMENTS],
      keepUnusedDataFor: 60 * 5,
      async onQueryStarted(
        request: PaymentMethodsQueryArgs,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data, meta } = await queryFulfilled;
          if (meta?.response?.status === 200) {
            const depositMethodsApiOrder = transformPaymentMethods(
              data.paymentMethods,
              "deposit",
            );
            const withdrawMethodsApiOrder = transformPaymentMethods(
              data.paymentMethods,
              "withdraw",
            );
            dispatch(setDepositMethods(depositMethodsApiOrder));
            dispatch(setWithdrawMethods(withdrawMethodsApiOrder));
            dispatch(setServiceError(false));
          }
        } catch (error) {
          dispatch(setServiceError(true));
        }
      },
    }),
    crypto: build.query<BanksApiResponse, BanksMethodsQueryArgs>({
      query: (args) => ({
        url: url.config.api.payments.banks,
        params: {
          paymentMethods: args.paymentMethods,
        },
      }),
      providesTags: [API_TAGS.PAYMENTS],
      keepUnusedDataFor: 60 * 5,
      async onQueryStarted(
        request: BanksMethodsQueryArgs,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data, meta } = await queryFulfilled;
          if (meta?.response?.status === 200) {
            dispatch(setCryptoMethods(data));
            dispatch(setServiceError(false));
          }
        } catch (error) {
          dispatch(setServiceError(true));
        }
      },
    }),
    cryptoDepositCreate: build.mutation<
      CryptoDepositResponse,
      CryptoDepositRequest
    >({
      query: (request: CryptoDepositRequest) => ({
        url: url.config.api.payments.cryptoDeposit,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: CryptoDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          if (data.statusCode === 200) {
            dispatchTransactionStatus(dispatch, data);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    paykassmaDepositCreate: build.mutation<
      PaykassmaDepositCreateResponse,
      PaykassmaDepositCreateRequest
    >({
      query: (request: PaykassmaDepositCreateRequest) => ({
        url: url.config.api.payments.paykassmaDepositCreate(request.method),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: PaykassmaDepositCreateRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          dispatchTransactionStatus(dispatch, data);
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    paykassmaDepositActivate: build.mutation<
      PaykassmaDepositActivateResponse,
      PaykassmaDepositActivateRequest
    >({
      query: (request: PaykassmaDepositActivateRequest) => ({
        url: url.config.api.payments.paykassmaDepositActivate,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: PaykassmaDepositActivateRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    paykassmaWithdraw: build.mutation<
      PaykassmaWithdrawResponse,
      PaykassmaWithdrawRequest
    >({
      query: (request: PaykassmaWithdrawRequest) => ({
        url: url.config.api.payments.paykassmaWithdraw(request.paymentSystem),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: PaykassmaWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    simplepayDeposit: build.mutation<
      SimplepayDepositResponse,
      SimplepayDepositRequest
    >({
      query: (request: SimplepayDepositRequest) => ({
        url: url.config.api.payments.simplepayDeposit(request.method),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: SimplepayDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    simplepayWithdraw: build.mutation<
      SimplepayWithdrawResponse,
      SimplepayWithdrawRequest
    >({
      query: (request: SimplepayWithdrawRequest) => ({
        url: url.config.api.payments.simplepayWithdraw(request.paymentSystem),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: SimplepayWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    constantposDeposit: build.mutation<
      ConstantposDepositResponse,
      ConstantposDepositRequest
    >({
      query: (request: ConstantposDepositRequest) => ({
        url: url.config.api.payments.constantposDeposit,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: ConstantposDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    constantposWithdraw: build.mutation<
      ConstantposWithdrawResponse,
      ConstantposWithdrawRequest
    >({
      query: (request: ConstantposWithdrawRequest) => ({
        url: url.config.api.payments.constantposWithdraw(request.paymentSystem),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: ConstantposWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    zenpayDeposit: build.mutation<ZenpayDepositResponse, ZenpayDepositRequest>({
      query: (request: ZenpayDepositRequest) => ({
        url: url.config.api.payments.zenpayDeposit,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: ZenpayDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    zenpayWithdraw: build.mutation<
      ZenpayWithdrawResponse,
      ZenpayWithdrawRequest
    >({
      query: (request: ZenpayWithdrawRequest) => ({
        url: url.config.api.payments.zenpayWithdraw,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: ZenpayWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    valerpayDeposit: build.mutation<
      ValerpayDepositResponse,
      ValerpayDepositRequest
    >({
      query: (request: ValerpayDepositRequest) => ({
        url: url.config.api.payments.valerpayDeposit(request.method),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: ValerpayDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    valerpayWithdraw: build.mutation<
      ValerpayWithdrawResponse,
      ValerpayWithdrawRequest
    >({
      query: (request: ValerpayWithdrawRequest) => ({
        url: url.config.api.payments.valerpayWithdraw(request.paymentSystem),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: ValerpayWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    wellxpayDeposit: build.mutation<
      WellxpayDepositResponse,
      WellxpayDepositRequest
    >({
      query: (request: WellxpayDepositRequest) => ({
        url: url.config.api.payments.wellxpayDeposit(request.method),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: WellxpayDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    wellxpayWithdraw: build.mutation<
      WellxpayWithdrawResponse,
      WellxpayWithdrawRequest
    >({
      query: (request: WellxpayWithdrawRequest) => ({
        url: url.config.api.payments.wellxpayWithdraw(request.paymentSystem),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: WellxpayWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    ccDeposit: build.mutation<MpayDepositResponse, MpayDepositRequest>({
      query: (request: MpayDepositRequest) => ({
        url: url.config.api.payments.ccDeposit,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: MpayDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    troyDeposit: build.mutation<TroyDepositResponse, TroyDepositRequest>({
      query: (request: TroyDepositRequest) => ({
        url: url.config.api.payments.troyDeposit,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: TroyDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    upiFastVDeposit: build.mutation<
      UpiVFastDepositResponse,
      UpiVFastDepositRequest
    >({
      query: (request: UpiVFastDepositRequest) => ({
        url: url.config.api.payments.upiVFast,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: UpiVFastDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    paparaDeposit: build.mutation<PaparaDepositResponse, PaparaDepositRequest>({
      query: (request: PaparaDepositRequest) => ({
        url: url.config.api.payments.paparaDeposit,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: PaparaDepositRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    paparaWithdraw: build.mutation<
      PaparaWithdrawResponse,
      PaparaWithdrawRequest
    >({
      query: (request: PaparaWithdrawRequest) => ({
        url: url.config.api.payments.paparaWithdraw,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: PaparaWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    havaleVipDeposit: build.mutation<
      HavaleVipDepositResponse,
      HavaleVipDepositRequest
    >({
      query: (request: HavaleVipDepositRequest) => ({
        url: url.config.api.payments.havaleVipDeposit,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: PaparaDepositRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    havaleVipWithdraw: build.mutation<
      HavaleVipWithdrawResponse,
      HavaleVipWithdrawRequest
    >({
      query: (request: HavaleVipWithdrawRequest) => ({
        url: url.config.api.payments.havaleVipWithdraw,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: HavaleVipWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),

    expressHavaleDeposit: build.mutation<
      ExpressHavaleDepositResponse,
      ExpressHavaleDepositRequest
    >({
      query: (request: ExpressHavaleDepositRequest) => ({
        url: url.config.api.payments.expressHavaleDeposit,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: ExpressHavaleDepositRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    expressHavaleWithdraw: build.mutation<
      ExpressHavaleWithdrawResponse,
      ExpressHavaleWithdrawRequest
    >({
      query: (request: ExpressHavaleWithdrawRequest) => ({
        url: url.config.api.payments.expressHavaleWithdraw,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: ExpressHavaleWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    havaleHeraDeposit: build.mutation<
      HavaleHeraDepositResponse,
      HavaleHeraDepositRequest
    >({
      query: (request: HavaleHeraDepositRequest) => ({
        url: url.config.api.payments.havaleHeraDeposit,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: HavaleHeraDepositRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    havaleHeraWithdraw: build.mutation<
      HavaleHeraWithdrawResponse,
      HavaleHeraWithdrawRequest
    >({
      query: (request: HavaleHeraWithdrawRequest) => ({
        url: url.config.api.payments.havaleHeraWithdraw,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: HavaleHeraWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    havaleHeraBanks: build.query<BanksResponse, string>({
      query: () => ({
        url: url.config.api.payments.havaleHeraBanks,
      }),
      async onQueryStarted(
        request: string,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data } = await queryFulfilled;
          if (data.statusCode === 200) {
            dispatch(setBanks(data.banks));
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    anindaHavaleDeposit: build.mutation<
      AnindaHavaleDepositResponse,
      AnindaHavaleDepositRequest
    >({
      query: (request: AnindaHavaleDepositRequest) => ({
        url: url.config.api.payments.anindaHavaleDeposit,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: AnindaHavaleDepositRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    anindaHavaleWithdraw: build.mutation<
      AnindaHavaleWithdrawResponse,
      AnindaHavaleWithdrawRequest
    >({
      query: (request: AnindaHavaleWithdrawRequest) => ({
        url: url.config.api.payments.anindaHavaleWithdraw,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: AnindaHavaleWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    epulDeposit: build.mutation<EpulDepositResponse, EpulDepositRequest>({
      query: (request: EpulDepositRequest) => ({
        url: url.config.api.payments.epulDeposit,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: EpulDepositRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    epulWithdraw: build.mutation<EpulWithdrawResponse, EpulWithdrawRequest>({
      query: (request: EpulWithdrawRequest) => ({
        url: url.config.api.payments.epulWithdraw,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: EpulWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    paycosDeposit: build.mutation<PaycosDepositResponse, PaycosDepositRequest>({
      query: (request: PaycosDepositRequest) => ({
        url: url.config.api.payments.paycosDeposit,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: PaycosDepositRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    paycosWithdraw: build.mutation<
      PaycosWithdrawResponse,
      PaycosWithdrawRequest
    >({
      query: (request: PaycosWithdrawRequest) => ({
        url: url.config.api.payments.paycosWithdraw,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: PaycosWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    bankCardsDeposit: build.mutation<
      BankCardsDepositResponse,
      BankCardsDepositRequest
    >({
      query: (request: BankCardsDepositRequest) => ({
        url: url.config.api.payments.bankCardsDeposit,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: BankCardsDepositRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    mefeteDeposit: build.mutation<MefeteDepositResponse, MefeteDepositRequest>({
      query: (request: MefeteDepositRequest) => ({
        url: url.config.api.payments.mefeteDeposit,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: MefeteDepositRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    mefeteWithdraw: build.mutation<
      MefeteWithdrawResponse,
      MefeteWithdrawRequest
    >({
      query: (request: MefeteWithdrawRequest) => ({
        url: url.config.api.payments.mefeteWithdraw,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: MefeteWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    payfixDeposit: build.mutation<PayfixDepositResponse, PayfixDepositRequest>({
      query: (request: PayfixDepositRequest) => ({
        url: url.config.api.payments.payfixDeposit,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: PayfixDepositRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    payfixWithdraw: build.mutation<
      PayfixWithdrawResponse,
      PayfixWithdrawRequest
    >({
      query: (request: PayfixWithdrawRequest) => ({
        url: url.config.api.payments.payfixWithdraw,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: PayfixWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    wangupayDeposit: build.mutation<
      WangupayDepositResponse,
      WangupayDepositRequest
    >({
      query: (request: WangupayDepositRequest) => ({
        url: url.config.api.payments.wangupayDeposit(request.method),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: WangupayDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    cashixDeposit: build.mutation<CashixDepositResponse, CashixDepositRequest>({
      query: (request: CashixDepositRequest) => ({
        url: url.config.api.payments.cashixDeposit(request.method),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: CashixDepositRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          dispatchTransactionStatus(dispatch, data);
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    cashixWithdraw: build.mutation<
      CashixWithdrawResponse,
      CashixWithdrawRequest
    >({
      query: (request: CashixWithdrawRequest) => ({
        url: url.config.api.payments.cashixWithdraw(request.paymentSystem),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: CashixWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    wangupayWithdraw: build.mutation<
      WangupayWithdrawResponse,
      WangupayWithdrawRequest
    >({
      query: (request: WangupayWithdrawRequest) => ({
        url: url.config.api.payments.wangupayWithdraw(request.paymentSystem),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: WangupayWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    corefyDeposit: build.mutation<CorefyDepositResponse, CorefyDepositRequest>({
      query: (request: CorefyDepositRequest) => ({
        url: url.config.api.payments.corefyDeposit(request.method),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: CorefyDepositRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const state: RootState = getState() as RootState;
          const currency = currentCurrency(state);

          const { data } = await queryFulfilled;
          handlerPaymentMethodAction(request?.method, data, dispatch, currency);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    corefyWithdraw: build.mutation<
      CorefyWithdrawResponse,
      CorefyWithdrawRequest
    >({
      query: (request: CorefyWithdrawRequest) => ({
        url: url.config.api.payments.corefyWithdraw(request.paymentSystem),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: CorefyWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    exPayWithdraw: build.mutation<ExPayWithdrawResponse, ExPayWithdrawRequest>({
      query: (request: ExPayWithdrawRequest) => ({
        url: url.config.api.payments.exPayWithdraw,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: ExPayWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    apayDeposit: build.mutation<ApayDepositResponse, ApayDepositRequest>({
      query: (request: ApayDepositRequest) => ({
        url: url.config.api.payments.apayDeposit(request.method),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: ApayDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    apayWithdraw: build.mutation<ApayWithdrawResponse, ApayWithdrawRequest>({
      query: (request: ApayWithdrawRequest) => ({
        url: url.config.api.payments.apayWithdraw(request.paymentSystem),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: ApayWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    azarpayDeposit: build.mutation<
      AzarpayDepositResponse,
      AzarpayDepositRequest
    >({
      query: (request: AzarpayDepositRequest) => ({
        url: url.config.api.payments.azarpayDeposit,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: AzarpayDepositRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data } = await queryFulfilled;
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    azarpayWithdraw: build.mutation<
      AzarpayWithdrawResponse,
      AzarpayWithdrawRequest
    >({
      query: (request: AzarpayWithdrawRequest) => ({
        url: url.config.api.payments.azarpayWithdraw,
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: AzarpayWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    azarpayBanks: build.query<BanksResponse, string>({
      query: () => ({
        url: url.config.api.payments.azarpayBanks,
      }),
      async onQueryStarted(
        request: string,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { data } = await queryFulfilled;
          if (data.statusCode === 200) {
            dispatch(setBanks(data.banks));
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    smartProPayDeposit: build.mutation<
      SmartProPayDepositResponse,
      SmartProPayDepositRequest
    >({
      query: (request: SmartProPayDepositRequest) => ({
        url: url.config.api.payments.smartProPayDeposit(request.method),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: SmartProPayDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    smartProPayWithdraw: build.mutation<
      SmartProPayWithdrawResponse,
      SmartProPayWithdrawRequest
    >({
      query: (request: SmartProPayWithdrawRequest) => ({
        url: url.config.api.payments.smartProPayWithdraw(request.paymentSystem),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: SmartProPayWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    maksiparaDeposit: build.mutation<
      MaksiparaDepositResponse,
      MaksiparaDepositRequest
    >({
      query: (request: MaksiparaDepositRequest) => ({
        url: url.config.api.payments.maksiparaDeposit(request.method),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: MaksiparaDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    payokDeposit: build.mutation<PayokDepositResponse, PayokDepositRequest>({
      query: (request: PayokDepositRequest) => ({
        url: url.config.api.payments.payokDeposit(request.method),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: PayokDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    vevopayDeposit: build.mutation<
      VevopayDepositResponse,
      VevopayDepositRequest
    >({
      query: (request: VevopayDepositRequest) => ({
        url: url.config.api.payments.vevopayDeposit(request.method),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: VevopayDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    maksiparaWithdraw: build.mutation<
      MaksiparaWithdrawResponse,
      MaksiparaWithdrawRequest
    >({
      query: (request: MaksiparaWithdrawRequest) => ({
        url: url.config.api.payments.maksiparaWithdraw(request.paymentSystem),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: MaksiparaWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    octopaysDeposit: build.mutation<
      OctopaysDepositResponse,
      OctopaysDepositRequest
    >({
      query: (request: OctopaysDepositRequest) => ({
        url: url.config.api.payments.octopaysDeposit(request.method),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: OctopaysDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    octopaysWithdraw: build.mutation<
      OctopaysWithdrawResponse,
      OctopaysWithdrawRequest
    >({
      query: (request: OctopaysWithdrawRequest) => ({
        url: url.config.api.payments.octopaysWithdraw(request.paymentSystem),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: OctopaysWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    bitoloDeposit: build.mutation<BitoloDepositResponse, BitoloDepositRequest>({
      query: (request: BitoloDepositRequest) => ({
        url: url.config.api.payments.bitoloDeposit(request.method),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: BitoloDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    bitoloWithdraw: build.mutation<
      BitoloWithdrawResponse,
      BitoloWithdrawRequest
    >({
      query: (request: BitoloWithdrawRequest) => ({
        url: url.config.api.payments.bitoloWithdraw(request.paymentSystem),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: BitoloWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    bovaWithdraw: build.mutation<BovaWithdrawResponse, BovaWithdrawRequest>({
      query: (request: BovaWithdrawRequest) => ({
        url: url.config.api.payments.bovaWithdraw(request.paymentSystem),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: BovaWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    payokWithdraw: build.mutation<PayokWithdrawResponse, PayokWithdrawRequest>({
      query: (request: PayokWithdrawRequest) => ({
        url: url.config.api.payments.payokWithdraw(request.paymentSystem),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: PayokWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    inwizoDeposit: build.mutation<InwizoDepositResponse, InwizoDepositRequest>({
      query: (request: InwizoDepositRequest) => ({
        url: url.config.api.payments.inwizoDeposit(request.method),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: InwizoDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    inwizoWithdraw: build.mutation<
      InwizoWithdrawResponse,
      InwizoWithdrawRequest
    >({
      query: (request: InwizoWithdrawRequest) => ({
        url: url.config.api.payments.inwizoWithdraw(request.paymentSystem),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: InwizoWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),

    spayzDeposit: build.mutation<SpayzDepositResponse, SpayzDepositRequest>({
      query: (request: SpayzDepositRequest) => ({
        url: url.config.api.payments.spayzDeposit(request.method),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: SpayzDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    spayzWithdraw: build.mutation<SpayzWithdrawResponse, SpayzWithdrawRequest>({
      query: (request: SpayzWithdrawRequest) => ({
        url: url.config.api.payments.spayzWithdraw(request.paymentSystem),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: SpayzWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    easypaygameDeposit: build.mutation<
      EasypaygameDepositResponse,
      EasypaygameDepositRequest
    >({
      query: (request: EasypaygameDepositRequest) => ({
        url: url.config.api.payments.easypaygameDeposit(request.method),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: EasypaygameDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    easypaygameWithdraw: build.mutation<
      EasypaygameWithdrawResponse,
      EasypaygameWithdrawRequest
    >({
      query: (request: EasypaygameWithdrawRequest) => ({
        url: url.config.api.payments.easypaygameWithdraw(request.paymentSystem),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: EasypaygameWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    bovaDeposit: build.mutation<BovaDepositResponse, BovaDepositRequest>({
      query: (request: BovaDepositRequest) => ({
        url: url.config.api.payments.bovaDeposit(request.method),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: BovaDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    ummoneyDeposit: build.mutation<
      UmmoneyDepositResponse,
      UmmoneyDepositRequest
    >({
      query: (request: UmmoneyDepositRequest) => ({
        url: url.config.api.payments.ummoneyDeposit(request.method),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: UmmoneyDepositRequest,
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setLoading(false));
          handlerPaymentMethodAction(request?.method, data, dispatch);
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
    ummoneyWithdraw: build.mutation<
      UmmoneyWithdrawResponse,
      UmmoneyWithdrawRequest
    >({
      query: (request: UmmoneyWithdrawRequest) => ({
        url: url.config.api.payments.ummoneyWithdraw(request.paymentSystem),
        method: "POST",
        body: request,
      }),
      async onQueryStarted(
        request: UmmoneyWithdrawRequest,
        { dispatch, queryFulfilled, getState },
      ) {
        try {
          const { meta } = await queryFulfilled;
          dispatch(setLoading(false));
          if (
            meta &&
            meta.response &&
            typeof meta.response.status === "number"
          ) {
            statusHandler(dispatch, meta.response.status);
          }
        } catch (error: any) {
          dispatchError(error, dispatch);
        }
      },
    }),
  }),
});
